// Typed inspection entries that were not added yet ("Dodaj vnos ročno"), kept in localStorage so
// they survive leaving the page or a reload. A draft belongs to the user who typed it:
// `baam:manual-entry:<userId>:<docId>`, and only that user's pages read it. Without accounts (and in
// versions before them) the key is `baam:manual-entry:<docId>`; the first user who adopts such a
// device takes those over (see session.adoptUser). A user's logout removes that user's drafts
// (removeManualDrafts); another user signing in leaves them for when their author comes back, and
// they go only when that user's data is discarded (removeOtherManualDrafts).

const PREFIX = 'baam:manual-entry:';

export function manualDraftKey(userId: string | null, docId: string): string {
  return userId ? `${PREFIX}${userId}:${docId}` : `${PREFIX}${docId}`;
}

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    // Some browsers throw for just touching localStorage when storage is blocked.
    return null;
  }
}

function draftKeys(s: Storage): string[] {
  const keys: string[] = [];
  for (let i = 0; i < s.length; i++) {
    const key = s.key(i);
    if (key?.startsWith(PREFIX)) keys.push(key);
  }
  return keys;
}

/** The user a draft key belongs to; null for a key written without a user. */
function userOfKey(key: string): string | null {
  const rest = key.slice(PREFIX.length);
  const colon = rest.indexOf(':');
  return colon < 0 ? null : rest.slice(0, colon);
}

/** Number of non-empty drafts whose user (null: written without one) `select` picks. */
function countWhere(select: (user: string | null) => boolean): number {
  const s = storage();
  if (!s) return 0;
  try {
    return draftKeys(s).filter((key) => select(userOfKey(key)) && s.getItem(key)).length;
  } catch {
    return 0;
  }
}

function removeWhere(select: (user: string | null) => boolean): void {
  const s = storage();
  if (!s) return;
  try {
    for (const key of draftKeys(s)) if (select(userOfKey(key))) s.removeItem(key);
  } catch {
    // Nothing more can be done; the drafts are keyed by user and never shown to someone else.
  }
}

export function readManualDraft(userId: string | null, docId: string): string {
  try {
    return storage()?.getItem(manualDraftKey(userId, docId)) ?? '';
  } catch {
    return '';
  }
}

export function writeManualDraft(userId: string | null, docId: string, text: string): void {
  try {
    const s = storage();
    if (!s) return;
    if (text) s.setItem(manualDraftKey(userId, docId), text);
    else s.removeItem(manualDraftKey(userId, docId));
  } catch {
    // Storage unavailable (private mode, quota): the draft then only lives while the page is open.
  }
}

/** Number of non-empty drafts of `userId` on this device (told in the logout dialog). */
export function countManualDrafts(userId: string | null): number {
  return countWhere((user) => user === userId);
}

/** Number of non-empty drafts of other users than `userId` (named when their data is discarded). */
export function countOtherManualDrafts(userId: string): number {
  return countWhere((user) => user !== null && user !== userId);
}

/** At the logout of `userId`: removes that user's drafts and those written without a user. */
export function removeManualDrafts(userId: string | null): void {
  removeWhere((user) => user === null || user === userId);
}

/** Removes the drafts of every user but `userId` (their data is discarded, see session.discardForeignData). */
export function removeOtherManualDrafts(userId: string): void {
  removeWhere((user) => user !== userId);
}

/** Drafts written without a user (no accounts, or a version before them): docId -> text. */
export function legacyManualDrafts(): Map<string, string> {
  const out = new Map<string, string>();
  const s = storage();
  if (!s) return out;
  try {
    for (const key of draftKeys(s)) {
      const rest = key.slice(PREFIX.length);
      const text = s.getItem(key);
      if (!rest.includes(':') && text) out.set(rest, text);
    }
  } catch {
    // Unreadable: nothing to take over.
  }
  return out;
}
